import { useEffect, useState } from "react";
import { Loader2, ExternalLink, Sparkles, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { getRecommendations, generateBlurb } from "../lib/api";

export default function Recommendations() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [blurbs, setBlurbs] = useState({});
  const [blurbLoading, setBlurbLoading] = useState({});
  const [batchLoading, setBatchLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try { setData(await getRecommendations()); }
      finally { setLoading(false); }
    })();
  }, []);

  const loadBlurb = async (rec) => {
    if (blurbs[rec.mal_id] || blurbLoading[rec.mal_id]) return;
    setBlurbLoading((s) => ({ ...s, [rec.mal_id]: true }));
    try {
      const res = await generateBlurb({
        mal_id: rec.mal_id,
        title: rec.title,
        because_of: rec.because_you_watched?.[0],
      });
      setBlurbs((s) => ({ ...s, [rec.mal_id]: res.blurb }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Blurb unavailable");
    } finally {
      setBlurbLoading((s) => ({ ...s, [rec.mal_id]: false }));
    }
  };

  const loadAllBlurbs = async () => {
    setBatchLoading(true);
    try {
      const targets = (data?.recommendations || []).filter((r) => !blurbs[r.mal_id]);
      await Promise.all(targets.map((r) => loadBlurb(r)));
      toast.success("All blurbs generated");
    } finally {
      setBatchLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-32 gap-3">
        <Loader2 className="h-6 w-6 animate-spin text-[#FF3B30]" />
        <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Pulling recs from MyAnimeList...</p>
      </div>
    );
  }

  if (!data || data.empty) {
    return (
      <div className="mx-auto max-w-[1400px] px-6 py-16 md:px-10">
        <h1 className="font-display text-6xl uppercase text-white">No recs yet</h1>
        <p className="mt-3 text-neutral-500">Add &amp; rate a few anime first — we need something to work from.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1600px] px-6 py-10 md:px-10">
      <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-[#FF3B30]">// Vol. 03</p>
      <div className="mt-2 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="font-display text-6xl uppercase leading-[0.9] tracking-tight text-white md:text-8xl">
            Watch<br/>Next
          </h1>
          <p className="mt-4 max-w-lg text-sm text-neutral-400">
            Sourced from &quot;users who liked this also liked&quot; on your top-rated entries. Tap ✨ Why? for an AI pitch.
          </p>
        </div>
        {data.recommendations?.length > 0 && (
          <button
            data-testid="all-blurbs-btn"
            onClick={loadAllBlurbs}
            disabled={batchLoading}
            className="flex items-center gap-2 self-start border border-white/20 bg-white px-4 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-black hover:bg-[#FF3B30] hover:text-white disabled:opacity-50 md:self-end"
          >
            {batchLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
            Explain All
          </button>
        )}
      </div>

      {data.based_on?.length > 0 && (
        <div className="mt-8 flex flex-wrap items-center gap-3 border-y border-white/10 py-4">
          <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Seeded from:</span>
          {data.based_on.map((s) => (
            <span key={s.mal_id} className="border border-white/15 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-white">
              {s.title}
            </span>
          ))}
        </div>
      )}

      {data.recommendations?.length === 0 ? (
        <p className="mt-16 text-center font-mono text-xs uppercase tracking-widest text-neutral-500">
          No new recs — MyAnimeList didn&apos;t return matches yet.
        </p>
      ) : (
        <div data-testid="recs-grid" className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {data.recommendations.map((r, i) => (
            <div
              key={r.mal_id}
              data-testid={`rec-card-${r.mal_id}`}
              className="group flex flex-col border border-white/10 bg-neutral-950"
              style={{ animation: `fadeUp 0.5s ${i * 0.04}s both ease-out` }}
            >
              <a href={r.url} target="_blank" rel="noreferrer" className="relative block aspect-[3/2] overflow-hidden bg-neutral-900">
                {r.image_url && <img src={r.image_url} alt={r.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="absolute right-3 top-3 flex items-center gap-1 border border-white/20 bg-black/70 px-2 py-1 font-mono text-[9px] text-white backdrop-blur">
                  <Sparkles className="h-3 w-3 text-[#FF3B30]" /> {r.score}
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h3 className="line-clamp-2 text-lg font-semibold leading-tight text-white">{r.title}</h3>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-neutral-400">
                    Because: {r.because_you_watched?.[0]}
                  </p>
                </div>
              </a>

              <div className="flex flex-1 flex-col gap-3 p-4">
                {blurbs[r.mal_id] ? (
                  <p className="min-h-[3rem] text-sm leading-relaxed text-neutral-300">
                    <span className="mr-1 text-[#FF3B30]">✦</span>
                    {blurbs[r.mal_id]}
                  </p>
                ) : (
                  <button
                    data-testid={`blurb-btn-${r.mal_id}`}
                    onClick={() => loadBlurb(r)}
                    disabled={blurbLoading[r.mal_id]}
                    className="flex items-center justify-center gap-2 border border-white/15 bg-transparent py-2 font-mono text-[10px] uppercase tracking-widest text-neutral-300 hover:border-[#FF3B30] hover:text-[#FF3B30] disabled:opacity-50"
                  >
                    {blurbLoading[r.mal_id] ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
                    ✨ Why?
                  </button>
                )}
                <a
                  href={r.url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-between font-mono text-[9px] uppercase tracking-widest text-neutral-500 hover:text-white"
                >
                  MAL #{r.mal_id}
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
